
import ViewHeader from "@/components/ViewHeader.vue";
import { useStore } from "@/store";
import _ from "lodash";
import { computed, defineComponent, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { api } from "@/services/Api";
import { useI18n } from "vue-i18n";
import swal from "sweetalert2";
import { PaymentType, PrintSize } from "@/models/Enums";
import { Budget, Order } from "@/models/Order";
import mixin from "@/mixin";
import useOrder from "@/modules/useOrder";
import q from "q";
import { VIRTUAL_SLOT_ID } from '@/models/Constants';

export default defineComponent({
  components: { ViewHeader },
  props: {
    paymentIndex: {
      type: Number,
    },
  },
  async setup(props) {
    const formatAmount = mixin.methods.formatAmount;
    const { t } = useI18n();
    const { printReceipt } = useOrder();
    const router = useRouter();
    const showSaveOrder = computed(
      () => router.currentRoute.value.name == "selectPayment"
    );
    const store = useStore();
    const orderItem = store.state.order;
    const createdNow = computed(() => store.state.order.createdNow);
    const autoPrintReceipt = computed(() => store.state.autoPrintReceipt);
    const paid = computed(() => store.getters.orderPaid);
    const balance = computed(() => store.getters.orderBalance);
    const orderTotal = computed(() => store.getters.orderTotal);
    const payments = computed(() => store.state.order.payment.payments);
    const localCurrencyId = computed(() => store.state.localCurrencyId);
    const orderCurrencyId = computed(() => store.state.order.currencyId);
    const groupedPayments = computed(() => {
      return _.groupBy(store.state.order.payment.payments, "paymentType");
    });

    function deletePayment(index: number) {
      if (
        router.currentRoute.value.params.paymentIndex != undefined &&
        router.currentRoute.value.params.paymentIndex.toString() ==
        index.toString()
      ) {
        router.replace({ name: "selectPayment" });
      }
      store.state.order.payment.deletePayment(index);
    }

    function currencySign(id) {
      return store.state.currencies.find((x) => x.id == id)?.symbol;
    }
    function currencyRate(id) {
      return (
        store.state.currencyRates.find((x) => x.currencyId == id)?.rate ?? 1
      );
    }

    const groupByPayments = ref(false);

    async function createInvoice(orderId: number) {
      const result = await swal.fire({
        text: t("cashier.createInvoice", [
          mixin.methods.formatAmount(
            paid.value / currencyRate(orderCurrencyId)
          ),
          currencySign(localCurrencyId.value),
        ]),
        icon: "warning",
        iconColor: "",
        showCancelButton: true,
        cancelButtonText: t("cashier.label.no"),
        confirmButtonText: t("cashier.label.yes"),
        customClass: {
          confirmButton: "popup-bookit-button",
          cancelButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
      });
      if (result.isConfirmed) {
        const createInvoiceResponsePromise = api.CreateInvoice(orderId);
        const createInvoiceresponse = await createInvoiceResponsePromise;
        if (createInvoiceresponse.errorMessage) {
          await swal.fire({
            icon: "error",
            title: t("error-pop-up.oops"),
            text: createInvoiceresponse.errorMessage,
          });
        }
      }
    }

    async function SaveOrder() {
      const order: Order = JSON.parse(JSON.stringify(store.state.order));
      order.products.forEach((orderProduct) => {
        orderProduct.startDateTime =
          orderProduct.date + "T" + orderProduct.slotStartTime;
      });
      order.payment.orderId = order.id;
      order.payment.cashPayments = order.payment.cashPayments.filter(
        (x) => !x.id
      );
      order.payment.checkPayments = order.payment.checkPayments.filter(
        (x) => !x.id
      );
      order.payment.creditCardPayments =
        order.payment.creditCardPayments.filter((x) => !x.id);
      order.payment.bankTransferPayments =
        order.payment.bankTransferPayments.filter((x) => !x.id);
      order.ticketPrintSize = store.getters.printerSettings?.ticketPrinterSize ?? PrintSize.Check_8cm;
      order.receiptPrintSize = store.getters.printerSettings?.receiptPrinterSize ?? PrintSize.Check_8cm;
      order.voucherCode = store.state.scanValue;
      var isEqual = mixin.methods.orderChanged(store.state.order.products, store.state.originOrder.products);

      // we skip saving the order if it is reserved(existing order) and has not been changed
      order.skipSaveOrder = order.isReserved && isEqual;
      const response = await ClearOrder(order, _.cloneDeep(store.state.budgets));

      if (response) {
        store.state.order.createdNow = !store.state.order.id;
        store.state.order.id = response.orderId;
        store.state.order.orderNumber = response.orderNumber;

        if (createdNow.value && balance.value == 0 && response.generateInvoice) {
          await createInvoice(response.orderId);
        }

        if (createdNow.value && autoPrintReceipt.value && response.jobId) {
          await printReceipt(response.jobId);
        }
      }
      else {
        return;
      }
      store.commit("clearOrder");
      router.push({
        name: "orderSuccessfullyCreated",
        params: {
          orderId: response?.orderId,
          orderNumber: response?.orderNumber,
          jobId: response?.jobId ?? 0,
        },
      });
    }

    async function ClearOrder(order: Order, budgets: Budget[] | null) {
      swal.fire({
        didOpen: () => {
          swal.showLoading();
        },
        allowOutsideClick: false // Prevent closing by clicking outside
      });
      const delay = q.delay(400);
      const response = await api.ClearOrder(order, budgets);
      await delay;

      if (response.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          html: response.errorMessage,
        });
        return null;
      }
      if (!response.data) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: response.errorMessage,
        });
        return null;
      }
      if (response.data.notAvailableProducts && response.data.notAvailableProducts.length > 0) {
        swal.fire({
          icon: "warning",
          text: t("alert.exceeded-quantity"),
          customClass: {
            confirmButton: "save-button-wrapper popup-bookit-button my-1 px-4",
          },
          buttonsStyling: false,
        });
        return null;
      }
      if (response.data.voucherResult?.resultId) {
        swal.fire({
          icon: "error",
          html: response.data.voucherResult.errorMessage,
          customClass: {
            confirmButton: "save-button-wrapper popup-bookit-button my-1 px-4",
          },
          buttonsStyling: false,
        });
        return null;
      }
      swal.close();
      return {
        orderId: response.data.orderId,
        orderNumber: response.data.orderNumber,
        orderDetails: response.data.order,
        jobId: response.data.jobId,
        generateInvoice: response.data.generateInvoice
      };
    }

    function getPaymentType(id) {
      const type = PaymentType[Number(id)];
      return t(`cashier.label.${type.toLocaleLowerCase()}`);
    }

    function editPayment(idx) {
      const p = payments.value.find((x) => x.index == idx);

      switch (p?.paymentType) {
        case PaymentType.Cash:
          router.push({
            name: "cash",
            params: { paymentIndex: idx },
            force: true,
            replace: router.currentRoute.value.name == "cash",
          });
          break;
        case PaymentType.Check:
          router.push({
            name: "check",
            params: { paymentIndex: idx },
            force: true,
            replace: router.currentRoute.value.name == "check",
          });
          break;
        case PaymentType.CreditCard:
          router.push({
            name: "creditCard",
            params: { paymentIndex: idx },
            force: true,
            replace: router.currentRoute.value.name == "creditCard",
          });
          break;
        case PaymentType.BankTransfer:
          router.push({
            name: "bankTransfer",
            params: { paymentIndex: idx },
            force: true,
            replace: router.currentRoute.value.name == "bankTransfer",
          });
          break;
      }
    }

    const w = watch(
      () => props.paymentIndex,
      () => isEditing.effect.run()
    );
    const isEditing = computed(() => props.paymentIndex != undefined);

    return {
      editPayment,
      orderTotal,
      balance,
      paid,
      payments,
      deletePayment,
      showSaveOrder,
      SaveOrder,
      groupedPayments,
      groupByPayments,
      currencySign,
      getPaymentType,
      isEditing,
      w,
      localCurrencyId,
      orderCurrencyId,
      currencyRate,
      createdNow,
      orderItem,
      autoPrintReceipt,
      formatAmount
    };
  },
});
